// src/components/OnboardingVideoModal.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

const OnboardingVideoModal = ({ active, onClose, user, onStartInteractiveTour }) => {
  const [view, setView] = useState('welcome'); // 'welcome' | 'playing' | 'post'
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(100); // 100 seconds total for simulation
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [useLocalVideo, setUseLocalVideo] = useState(false);
  const [currentSceneIdx, setCurrentSceneIdx] = useState(-1);
  const [voices, setVoices] = useState([]);
  const [showSubtitles, setShowSubtitles] = useState(false);

  const videoRef = useRef(null);
  const progressContainerRef = useRef(null);

  // Onboarding simulation scenes
  const scenes = useMemo(() => [
    {
      start: 0,
      end: 12,
      title: "Welcome to Genesis Tech Engineers Onboarding Portal",
      script: "Welcome to the Genesis Tech Engineers Onboarding Portal. In this quick video, we will walk you through the ten simple steps to complete your self-onboarding process.",
      renderVisual: () => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '1rem', background: 'linear-gradient(135deg, #090b16 0%, #1e1b4b 60%, #311042 100%)', color: '#fff', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '4.5rem', animation: 'bounceSlow 3s infinite', filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.4))' }}>🚀</div>
          <h2 style={{ fontSize: '2.1rem', fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #ffffff, #c7d2fe, #f43f5e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.03em', textShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>Welcome to Genesis Tech Engineers</h2>
          <p style={{ fontSize: '0.95rem', color: '#cbd5e1', maxWidth: '520px', margin: 0, opacity: 0.9 }}>Your digital gateway to joining the team starts here. Let's take a quick look at the onboarding workflow.</p>
        </div>
      )
    },
    {
      start: 12,
      end: 24,
      title: "Steps 1 & 2: Personal Profile & Emergency Contacts",
      script: "First, you will enter your personal profile details, including address, pronouns, and emergency contacts. This ensures we can contact your loved ones if needed.",
      renderVisual: () => (
        <div style={{ display: 'flex', gap: '1.5rem', padding: '2rem', background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
          <div className="glass-card" style={{ flex: 1, padding: '1.25rem', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(99, 102, 241, 0.25)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)', borderRadius: '16px', borderLeft: '4px solid #6366f1', color: '#fff', animation: 'fadeIn var(--transition-normal)' }}>
            <h4 style={{ margin: 0, color: '#818cf8', fontSize: '0.95rem', fontWeight: 'bold' }}>👤 Personal Information</h4>
            <div style={{ height: '12px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', width: '80%' }}></div>
            <div style={{ height: '12px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', width: '90%' }}></div>
            <div style={{ height: '12px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', width: '70%' }}></div>
          </div>
          <div className="glass-card" style={{ flex: 1, padding: '1.25rem', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(244, 63, 94, 0.25)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)', borderRadius: '16px', borderLeft: '4px solid #f43f5e', color: '#fff', animation: 'fadeIn var(--transition-normal)' }}>
            <h4 style={{ margin: 0, color: '#f43f5e', fontSize: '0.95rem', fontWeight: 'bold' }}>📞 Emergency Contacts</h4>
            <div style={{ height: '12px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', width: '90%' }}></div>
            <div style={{ height: '12px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', width: '60%' }}></div>
            <div style={{ height: '12px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', width: '75%' }}></div>
          </div>
        </div>
      )
    },
    {
      start: 24,
      end: 36,
      title: "Step 3: Identification Document Uploads",
      script: "Second, upload scans of your government ID, passport, or work authorization documents. This securely verifies your details for standard I-9 processing.",
      renderVisual: () => (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem', background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)', height: '100%', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
          <h4 style={{ margin: 0, color: '#fff', fontSize: '1.1rem', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>📑 Document Upload Center</h4>
          <div style={{ border: '2px dashed #6366f1', borderRadius: '12px', padding: '1.75rem', width: '75%', textAlign: 'center', background: 'rgba(15, 23, 42, 0.6)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)', position: 'relative' }}>
            <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem', animation: 'bounceSlow 2s infinite' }}>📤</span>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Drag and drop Passport or Driver's License here</span>
            <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#10b981', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
              <span>✓</span> passport_scan.pdf uploaded successfully!
            </div>
          </div>
        </div>
      )
    },
    {
      start: 36,
      end: 48,
      title: "Steps 4 & 5: Review & Sign Agreements",
      script: "Next, read and electronically sign your official Job Offer Letter and Employment Agreement. Our digital signature pad saves your signed copy automatically.",
      renderVisual: () => (
        <div style={{ display: 'flex', gap: '1.5rem', padding: '2rem', background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
          <div className="glass-card" style={{ flex: 1.1, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', height: '90%', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', overflow: 'hidden' }}>
            <h5 style={{ margin: 0, color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 'bold' }}>Offer_Letter.pdf</h5>
            <div style={{ flex: 1, background: 'rgba(15, 23, 42, 0.5)', borderRadius: '4px', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <div style={{ height: '5px', background: 'rgba(255,255,255,0.15)', width: '90%' }}></div>
              <div style={{ height: '5px', background: 'rgba(255,255,255,0.15)', width: '85%' }}></div>
              <div style={{ height: '5px', background: 'rgba(255,255,255,0.15)', width: '40%' }}></div>
              <div style={{ height: '5px', background: 'rgba(255,255,255,0.15)', width: '95%', marginTop: '6px' }}></div>
              <div style={{ height: '5px', background: 'rgba(255,255,255,0.15)', width: '70%' }}></div>
            </div>
          </div>
          <div className="glass-card" style={{ flex: 0.9, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', justifyContent: 'center', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(99, 102, 241, 0.25)', borderRadius: '12px' }}>
            <h5 style={{ margin: 0, color: '#818cf8', fontSize: '0.85rem', fontWeight: 'bold' }}>E-Signature Pad</h5>
            <div style={{ border: '1px solid rgba(255,255,255,0.15)', height: '80px', borderRadius: '4px', background: 'rgba(15, 23, 42, 0.8)', position: 'relative', overflow: 'hidden' }}>
              <svg width="100%" height="100%" style={{ stroke: '#818cf8', strokeWidth: 3, fill: 'none' }}>
                <path d="M 20 50 Q 60 20, 100 60 T 170 40" style={{ strokeDasharray: 300, strokeDashoffset: 0, animation: 'arrowPulse 3s infinite' }} />
              </svg>
            </div>
            <span style={{ fontSize: '0.65rem', color: '#94a3b8', textAlign: 'center' }}>Signed digitally</span>
          </div>
        </div>
      )
    },
    {
      start: 48,
      end: 60,
      title: "Step 6: Corporate Policies Review",
      script: "Review and accept corporate policy agreements such as the IT Security and Employee Handbook. Simply read and flip the confirmation switch for each policy.",
      renderVisual: () => (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem', background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)', height: '100%', justifyContent: 'center', gap: '0.75rem' }}>
          <h4 style={{ margin: 0, color: '#fff', textAlign: 'center', fontSize: '1.1rem', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>📋 Company Policies Checklist</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div className="glass-card" style={{ padding: '0.675rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}>
              <span style={{ fontSize: '0.8rem', color: '#f3f4f6' }}>1. IT & Information Security Policy</span>
              <span style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                Accepted ✓
              </span>
            </div>
            <div className="glass-card" style={{ padding: '0.675rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}>
              <span style={{ fontSize: '0.8rem', color: '#f3f4f6' }}>2. Employee Code of Conduct & Handbook</span>
              <span style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                Accepted ✓
              </span>
            </div>
          </div>
        </div>
      )
    },
    {
      start: 60,
      end: 72,
      title: "Steps 7 & 8: Taxes & Payroll Setup",
      script: "Configure your W-4 tax withholdings and fill in your bank routing and account numbers so your paycheck direct deposits are ready starting from your first day.",
      renderVisual: () => (
        <div style={{ display: 'flex', gap: '1.5rem', padding: '2rem', background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
          <div className="glass-card" style={{ flex: 1, padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(99, 102, 241, 0.25)', borderRadius: '12px', borderTop: '3px solid #6366f1', color: '#fff' }}>
            <h5 style={{ margin: 0, color: '#818cf8', fontSize: '0.9rem', fontWeight: 'bold' }}>💵 Tax Elections</h5>
            <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>Federal W-4 Completed</div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Status: Single / Standard</div>
          </div>
          <div className="glass-card" style={{ flex: 1, padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(244, 63, 94, 0.25)', borderRadius: '12px', borderTop: '3px solid #f43f5e', color: '#fff' }}>
            <h5 style={{ margin: 0, color: '#f43f5e', fontSize: '0.9rem', fontWeight: 'bold' }}>🏦 Bank Deposits</h5>
            <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>Chase Bank Setup</div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Routing: ••••0210</div>
          </div>
        </div>
      )
    },
    {
      start: 72,
      end: 84,
      title: "Step 9: Security Badge Photo Upload",
      script: "Upload a formal photo for your security badge. Make sure it has a plain, light background and shows your face clearly.",
      renderVisual: () => (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem', background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)', height: '100%', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
          <h4 style={{ margin: 0, color: '#fff', fontSize: '1rem', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>🪪 Corporate Security Badge</h4>
          <div style={{ display: 'flex', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '14px', padding: '1.25rem', width: '250px', background: 'rgba(30, 41, 59, 0.7)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4)', gap: '1rem', alignItems: 'center' }}>
            <div style={{ width: '70px', height: '90px', borderRadius: '8px', background: 'linear-gradient(135deg, #818cf8, #f43f5e)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.2)', fontSize: '2.5rem' }}>
              🧑‍💻
            </div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#ffffff' }}>{user?.name || 'New Hire'}</div>
              <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Team Member</div>
              <div style={{ fontSize: '0.6rem', color: '#818cf8', marginTop: '0.5rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>GENESIS TECH STAFF</div>
            </div>
          </div>
        </div>
      )
    },
    {
      start: 84,
      end: 100,
      title: "Step 10: Final Review & Submit",
      script: "Finally, review your completed onboarding file, double check for errors, and click Submit. Your HR representative will review and approve your profile. Thank you for choosing Genesis Tech Engineers, and welcome to the team!",
      renderVisual: () => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '0.75rem', background: 'linear-gradient(135deg, #064e3b 0%, #022c22 60%, #020617 100%)', color: '#fff', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3.5rem', animation: 'arrowPulse 2s infinite', filter: 'drop-shadow(0 0 10px rgba(16, 185, 129, 0.4))' }}>🎉</div>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: '#34d399', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>Onboarding Checklist Complete</h3>
          <p style={{ fontSize: '0.85rem', color: '#cbd5e1', maxWidth: '520px', margin: '0 0 0.5rem 0', lineHeight: '1.4' }}>
            Thank you for choosing Genesis Tech Engineers, and welcome to the team!
          </p>
          <button className="btn btn-primary btn-pulse" style={{ padding: '0.75rem 2rem', background: '#10b981', borderColor: '#10b981', fontWeight: 'bold', boxShadow: '0 0 15px rgba(16, 185, 129, 0.4)' }}>
            🚀 Submit to HR Verification
          </button>
        </div>
      )
    }
  ], [user]);

  // Fetch synthesis voices on mount
  useEffect(() => {
    const updateVoices = () => {
      if ('speechSynthesis' in window) {
        setVoices(window.speechSynthesis.getVoices());
      }
    };
    updateVoices();
    if ('speechSynthesis' in window) {
      window.speechSynthesis.addEventListener('voiceschanged', updateVoices);
    }
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.removeEventListener('voiceschanged', updateVoices);
      }
    };
  }, []);

  // Detect local video file presence using HEAD request
  useEffect(() => {
    if (active) {
      fetch('/onboardingvideo.mov', { method: 'HEAD' })
        .then(res => {
          const contentType = res.headers.get('content-type');
          if (res.ok && contentType && contentType.startsWith('video/')) {
            setUseLocalVideo(true);
            setDuration(0); // will be read from video metadata
          } else {
            setUseLocalVideo(false);
            setDuration(100);
          }
        })
        .catch(() => {
          setUseLocalVideo(false);
          setDuration(100);
        });
    }
  }, [active]);

  // Reset view state when modal becomes active & cancel speech on unmount/deactivate
  useEffect(() => {
    if (active) {
      setView('welcome');
      setIsPlaying(false);
      setCurrentTime(0);
      setCurrentSceneIdx(-1);
      setPlaybackRate(1);
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, [active]);

  // Speech synthesis speaker function
  const speakText = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel(); // Stop current speech
      if (isMuted) return; // Don't speak if muted

      const utterance = new SpeechSynthesisUtterance(text);

      // Slightly increase pitch (1.18) and baseline rate (1.08) to sound enthusiastic and energetic
      utterance.pitch = 1.18;
      utterance.rate = playbackRate * 1.08;
      utterance.volume = volume;

      // Select voice: prioritize US or UK English female voices
      const femaleKeywords = ['zira', 'samantha', 'hazel', 'susan', 'victoria', 'female', 'karen', 'moira', 'tessa', 'veena', 'fiona'];

      let selectedVoice = voices.find(v => {
        const name = v.name.toLowerCase();
        const lang = v.lang.toLowerCase();
        const isFemale = femaleKeywords.some(keyword => name.includes(keyword));
        const isUSorUK = lang === 'en-us' || lang === 'en-gb' || lang.startsWith('en-us') || lang.startsWith('en-gb');
        return isFemale && isUSorUK;
      });

      // Fallback 1: Any English female voice
      if (!selectedVoice) {
        selectedVoice = voices.find(v => {
          const name = v.name.toLowerCase();
          const lang = v.lang.toLowerCase();
          return femaleKeywords.some(keyword => name.includes(keyword)) && lang.startsWith('en');
        });
      }

      // Fallback 2: Any US or UK English voice
      if (!selectedVoice) {
        selectedVoice = voices.find(v => {
          const lang = v.lang.toLowerCase();
          return lang.startsWith('en-us') || lang.startsWith('en-gb');
        });
      }

      // Fallback 3: Any English voice
      if (!selectedVoice) {
        selectedVoice = voices.find(v => v.lang.toLowerCase().startsWith('en'));
      }

      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      window.speechSynthesis.speak(utterance);
    }
  }, [voices, isMuted, playbackRate, volume]);

  // Clock ticks for simulation playback
  useEffect(() => {
    let timer;
    if (isPlaying && !useLocalVideo && view === 'playing') {
      timer = setInterval(() => {
        setCurrentTime(prev => {
          const next = prev + 0.1 * playbackRate;
          if (next >= 100) {
            setIsPlaying(false);
            setView('post');
            if ('speechSynthesis' in window) {
              window.speechSynthesis.cancel();
            }
            return 100;
          }
          return next;
        });
      }, 100);
    }
    return () => clearInterval(timer);
  }, [isPlaying, playbackRate, useLocalVideo, view]);

  // Synchronize simulation scene changes and voice narrator trigger
  useEffect(() => {
    if (view !== 'playing' || useLocalVideo) return;

    if (!isPlaying) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      return;
    }

    const idx = scenes.findIndex(s => currentTime >= s.start && currentTime < s.end);
    if (idx !== -1 && idx !== currentSceneIdx) {
      setCurrentSceneIdx(idx);
      speakText(scenes[idx].script);
    }
  }, [currentTime, isPlaying, useLocalVideo, currentSceneIdx, voices, isMuted, playbackRate, volume, view, scenes, speakText]);

  if (!active) return null;

  // Handle Play/Pause
  const togglePlay = () => {
    if (useLocalVideo && videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(err => console.log("Play failed: ", err));
      }
      setIsPlaying(!isPlaying);
    } else {
      const nextPlaying = !isPlaying;
      setIsPlaying(nextPlaying);
      if (!nextPlaying) {
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel();
        }
      } else {
        // Trigger voice synthesis for current position
        const idx = scenes.findIndex(s => currentTime >= s.start && currentTime < s.end);
        if (idx !== -1) {
          speakText(scenes[idx].script);
        }
      }
    }
  };

  // Local Video events handlers
  const handleTimeUpdate = () => {
    if (useLocalVideo && videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (useLocalVideo && videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    setView('post');
  };

  // Format time (mm:ss)
  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Handle Scrubbing/Seeking on progress bar
  const handleScrub = (e) => {
    if (progressContainerRef.current && duration) {
      const rect = progressContainerRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const targetTime = percentage * duration;

      setCurrentTime(targetTime);
      if (useLocalVideo && videoRef.current) {
        videoRef.current.currentTime = targetTime;
      } else {
        // Reset currentSceneIdx to force text-to-speech update on new timestamp
        setCurrentSceneIdx(-1);
      }
    }
  };

  // Handle Mute/Unmute
  const toggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (useLocalVideo && videoRef.current) {
      videoRef.current.muted = nextMuted;
    } else {
      if (nextMuted) {
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel();
        }
      } else {
        const idx = scenes.findIndex(s => currentTime >= s.start && currentTime < s.end);
        if (idx !== -1) {
          speakText(scenes[idx].script);
        }
      }
    }
  };

  // Handle Volume Change
  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (useLocalVideo && videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
    }
    setIsMuted(val === 0);
  };

  // Handle Speed Change
  const handleSpeedChange = (e) => {
    const speed = parseFloat(e.target.value);
    setPlaybackRate(speed);
    if (useLocalVideo && videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
  };

  // Handle Fullscreen toggle
  const toggleFullscreen = () => {
    const playerEl = document.querySelector('.premium-video-player');
    if (playerEl) {
      if (!isFullscreen) {
        if (playerEl.requestFullscreen) {
          playerEl.requestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
        setIsFullscreen(false);
      }
    }
  };

  const handleStartWatch = () => {
    setView('playing');
    setTimeout(() => {
      if (useLocalVideo && videoRef.current) {
        videoRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(e => console.log("Video autoplay blocked, waiting for user trigger", e));
      } else {
        setIsPlaying(true);
        // Direct speak
        speakText(scenes[0].script);
      }
    }, 100);
  };

  const handleSkipAll = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    localStorage.setItem(`onboarding_video_seen_${user?.id}`, 'true');
    sessionStorage.setItem(`onboarding_video_seen_${user?.id}`, 'true');
    onClose();
  };

  const handleSkipAndTour = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    localStorage.setItem(`onboarding_video_seen_${user?.id}`, 'true');
    sessionStorage.setItem(`onboarding_video_seen_${user?.id}`, 'true');
    onClose();
    if (onStartInteractiveTour) {
      onStartInteractiveTour();
    }
  };

  const handlePostProceed = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    localStorage.setItem(`onboarding_video_seen_${user?.id}`, 'true');
    sessionStorage.setItem(`onboarding_video_seen_${user?.id}`, 'true');
    onClose();
  };

  // Active scene and progress percentage
  const activeScene = scenes.find(s => currentTime >= s.start && currentTime < s.end) || scenes[0];
  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="video-modal-overlay">
      <div className="video-modal-card">

        {/* Header */}
        <div className="video-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.25rem' }}>🎥</span>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)' }}>
              {view === 'welcome' && "Welcome to Genesis Tech Engineers Onboarding"}
              {view === 'playing' && "Onboarding Portal Walkthrough Video"}
              {view === 'post' && "Ready to Get Started!"}
            </h3>
          </div>
          <button
            onClick={handleSkipAll}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.2s'
            }}
            title="Close & Skip"
          >
            ✕
          </button>
        </div>

        {/* Content Body */}
        <div className="video-modal-body" style={{ minHeight: '380px', justifyContent: 'center' }}>

          {/* VIEW: Welcome and Choice */}
          {view === 'welcome' && (
            <div className="video-tour-prompt-container">
              <div className="prompt-icon-container">
                <span className="prompt-icon">🚀</span>
              </div>
              <h2 className="prompt-title">Your Onboarding Journey Starts Here!</h2>
              {/* <p className="prompt-description">
                Welcome to <strong>Genesis Tech Engineers Onboarding Portal</strong>, <strong>{user?.name || 'Valued Team Member'}</strong>! We've prepared a comprehensive, voiced video tour detailing the entire 10-step self-onboarding flow. Watch it now to glide through the setup smoothly, or skip to dive in.
              </p> */}

              {/* <div className="prompt-feature-grid">
                <div className="prompt-feature-item">
                  <span className="prompt-feature-emoji">🎙️</span>
                  <div className="prompt-feature-title">Voice Narrated</div>
                  <div className="prompt-feature-desc">Clear spoken instructions guiding you through forms & settings.</div>
                </div>
                <div className="prompt-feature-item">
                  <span className="prompt-feature-emoji">📑</span>
                  <div className="prompt-feature-title">10 Easy Steps</div>
                  <div className="prompt-feature-desc">Interactive roadmap outlining direct deposits, agreements & tax elections.</div>
                </div>
                <div className="prompt-feature-item">
                  <span className="prompt-feature-emoji">⚡</span>
                  <div className="prompt-feature-title">Secure & Simple</div>
                  <div className="prompt-feature-desc">Safe digital signatures and document uploads, approved in minutes.</div>
                </div>
              </div> */}

              <div className="prompt-actions">
                <button
                  onClick={handleSkipAndTour}
                  className="btn btn-secondary"
                  style={{ padding: '0.75rem 1.5rem', fontWeight: 600 }}
                >
                  🗺️ Interactive Tour Only
                </button>
                <button
                  onClick={handleStartWatch}
                  className="btn btn-primary btn-pulse"
                  style={{ padding: '0.75rem 2rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <span>▶️ Watch Video Tour (Voice)</span>
                </button>
              </div>
            </div>
          )}

          {/* VIEW: Video Player */}
          {view === 'playing' && (
            <div className="premium-video-player">

              {/* Voice Guided Pulse Badge */}
              <div className="voice-badge">
                <div className="voice-badge-pulse"></div>
                <span className="voice-badge-text">Narrated Walkthrough</span>
              </div>

              {/* RENDER MODE: HTML5 Video Element */}
              {useLocalVideo ? (
                <video
                  ref={videoRef}
                  src="/onboardingvideo.mov"
                  onClick={togglePlay}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleVideoEnded}
                  onError={() => {
                    console.warn("Local video playback failed or not supported, falling back to simulated tour.");
                    setUseLocalVideo(false);
                    setDuration(100);
                  }}
                  playsInline
                />
              ) : (
                /* RENDER MODE: Beautiful CSS Simulated Screens walkthrough */
                <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: 'var(--bg-tertiary)' }}>
                  {activeScene.renderVisual()}

                  {/* Subtitle / Script Caption Overlay at the bottom */}
                  {showSubtitles && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '50px',
                        left: '5%',
                        right: '5%',
                        background: 'rgba(15, 23, 42, 0.85)',
                        backdropFilter: 'blur(8px)',
                        color: '#fff',
                        padding: '0.75rem 1.25rem',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        textAlign: 'center',
                        lineHeight: '1.4',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                        animation: 'fadeIn 0.3s ease',
                        zIndex: 11
                      }}
                    >
                      <strong style={{ color: 'var(--primary-hover)', display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                        {activeScene.title}
                      </strong>
                      {activeScene.script}
                    </div>
                  )}
                </div>
              )}

              {/* Big Play Overlay (Centered) when Paused */}
              {!isPlaying && (
                <button className="player-big-play" onClick={togglePlay}>
                  <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"></path>
                  </svg>
                </button>
              )}

              {/* Video Custom Controller Bar */}
              <div className="player-controls-bar">

                {/* Scrub/Progress Bar */}
                <div
                  className="player-progress-container"
                  ref={progressContainerRef}
                  onClick={handleScrub}
                >
                  <div className="player-progress-bar" style={{ width: `${progressPercent}%` }}>
                    <div className="player-progress-handle"></div>
                  </div>
                </div>

                <div className="player-controls-row">

                  {/* Left Group: Play, Time, Volume */}
                  <div className="player-controls-left">
                    <button className="player-btn" onClick={togglePlay} title={isPlaying ? "Pause" : "Play"}>
                      {isPlaying ? (
                        <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path>
                        </svg>
                      ) : (
                        <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"></path>
                        </svg>
                      )}
                    </button>

                    {/* Time Counter */}
                    <span className="player-time-display">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>

                    {/* Volume Mute & Slider */}
                    <div className="volume-control">
                      <button className="player-btn" onClick={toggleMute} title={isMuted ? "Unmute" : "Mute"}>
                        {isMuted || volume === 0 ? (
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.21.05-.42.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.03c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73 4.27 3zM12 4L9.91 6.09 12 8.18V4z"></path>
                          </svg>
                        ) : volume < 0.5 ? (
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M7 9v6h4l5 5V4l-5 5H7zm11.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"></path>
                          </svg>
                        ) : (
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 9v6h4l5 5V4l-5 5H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"></path>
                          </svg>
                        )}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={volume}
                        onChange={handleVolumeChange}
                        className="volume-slider"
                      />
                    </div>
                  </div>

                  {/* Right Group: Playback Rate & Fullscreen */}
                  <div className="player-controls-right">

                    {/* Playback Rate Selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.65rem', color: '#ccc' }}>Speed:</span>
                      <select
                        value={playbackRate}
                        onChange={handleSpeedChange}
                        className="player-select"
                      >
                        <option value="0.75">0.75x</option>
                        <option value="1">1.0x</option>
                        <option value="1.25">1.25x</option>
                        <option value="1.5">1.5x</option>
                        <option value="2">2.0x</option>
                      </select>
                    </div>

                    {/* Closed Captions Toggle Button */}
                    {/* <button
                      className="player-btn"
                      onClick={() => setShowSubtitles(!showSubtitles)}
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        color: showSubtitles ? '#818cf8' : 'rgba(255, 255, 255, 0.8)',
                        background: showSubtitles ? 'rgba(99, 102, 241, 0.15)' : 'none',
                        border: showSubtitles ? '1px solid #818cf8' : '1px solid rgba(255, 255, 255, 0.25)',
                        padding: '0.2rem 0.4rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        lineHeight: 1
                      }}
                      title="Toggle Subtitles"
                    >
                      CC
                    </button> */}

                    {/* Fullscreen Button */}
                    <button className="player-btn" onClick={toggleFullscreen} title="Toggle Fullscreen">
                      <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"></path>
                      </svg>
                    </button>
                  </div>

                </div>
              </div>

            </div>
          )}

          {/* VIEW: Post-Video Screen */}
          {view === 'post' && (
            <div className="video-tour-prompt-container" style={{ padding: '1rem 1.5rem' }}>
              <div className="prompt-icon-container" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(99, 102, 241, 0.15))' }}>
                <span className="prompt-icon">🎉</span>
              </div>
              <h2 className="prompt-title" style={{ color: 'var(--success)' }}>Thank You & Welcome!</h2>
              <p className="prompt-description">
                Thank you for choosing Genesis Tech Engineers! We are thrilled to welcome you to the team. What would you like to do next?
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', maxWidth: '440px', marginTop: '1rem' }}>
                <button
                  onClick={handleSkipAndTour}
                  className="btn btn-primary"
                  style={{ padding: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                >
                  🗺️ Start Step-by-Step Guided Tour
                </button>
                <button
                  onClick={handlePostProceed}
                  className="btn btn-secondary"
                  style={{ padding: '0.8rem', fontWeight: 600 }}
                >
                  🚀 Go Straight to Dashboard
                </button>
                <button
                  onClick={() => setView('playing')}
                  style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}
                >
                  🎥 Replay Video Tour
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Footer info message */}
        <div style={{ padding: '0.75rem 1.75rem', backgroundColor: 'var(--bg-tertiary)', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
            You can replay this video tour at any time from the roadmap panel.
          </span>
          {view === 'playing' && (
            <button
              onClick={() => {
                if ('speechSynthesis' in window) {
                  window.speechSynthesis.cancel();
                }
                setView('post');
              }}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
            >
              Skip Video →
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default OnboardingVideoModal;
